import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const TopbarSlotsContext = createContext(null);

function removeFromArray(arr, value) {
  const idx = arr.indexOf(value);
  if (idx === -1) return arr;
  return [...arr.slice(0, idx), ...arr.slice(idx + 1)];
}

export function TopbarSlotsProvider({ children }) {
  const [stack, setStack] = useState([]);
  const [slotsById, setSlotsById] = useState({});

  const setSlots = useCallback((id, slots) => {
    if (!id) return;

    setSlotsById((prev) => ({ ...prev, [id]: slots || {} }));
    setStack((prev) => {
      // ensure "latest writer wins"
      const without = removeFromArray(prev, id);
      return [...without, id];
    });
  }, []);

  const clearSlots = useCallback((id) => {
    if (!id) return;

    setSlotsById((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
      // eslint-disable-next-line no-unused-vars
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    setStack((prev) => removeFromArray(prev, id));
  }, []);

  const activeId = stack.length ? stack[stack.length - 1] : null;
  const activeSlots = (activeId && slotsById[activeId]) || {};

  const value = useMemo(
    () => ({
      activeId,
      slots: activeSlots,
      setSlots,
      clearSlots,
    }),
    [activeId, activeSlots, setSlots, clearSlots]
  );

  return <TopbarSlotsContext.Provider value={value}>{children}</TopbarSlotsContext.Provider>;
}

TopbarSlotsProvider.propTypes = {
  children: PropTypes.node,
};

export function useTopbarSlots() {
  const ctx = useContext(TopbarSlotsContext);
  if (!ctx) throw new Error('useTopbarSlots must be used within TopbarSlotsProvider');
  return ctx;
}
